// Auth layer — BFF model. The browser holds NO OAuth state: portal-api runs the
// OIDC flow server-side and issues an httpOnly session cookie. The SPA only:
//   - redirects to /api/auth/login to sign in,
//   - forwards the IdP's ?code&state from /auth/callback to /api/auth/callback
//     (the OAuth client's registered redirect_uri is on the SPA host),
//   - reads /api/auth/session to know if it's signed in,
//   - POSTs /api/auth/logout to sign out, then follows the returned logoutUrl
//     to end login.divako.io's SSO session too.
// The cookie rides along automatically (same-site, *.divako.dev), so api.ts just
// sends `credentials: 'include'`. See CLAUDE.md → Authentication.

import { config } from './config';

export type Session = {
  authenticated: boolean;
  /**
   * Whether the backend's web auth is reachable. `false` means
   * `/api/auth/session` 404'd or errored (web auth disabled / backend not
   * configured) — so we must NOT bounce the user to a dead `/api/auth/login`.
   * Mirrors the old portal: query first (`isLogin()`), then decide.
   */
  available: boolean;
  uid?: string;
  email?: string;
};

/**
 * Ask portal-api whether the current cookie is a valid session (the BFF
 * equivalent of the old portal's `isLogin()`). Distinguishes "logged out"
 * (200 `{authenticated:false}`) from "auth unavailable" (404/error).
 */
export async function fetchSession(): Promise<Session> {
  try {
    const res = await fetch(`${config.apiBaseUrl}/auth/session`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return { authenticated: false, available: false };
    const data = (await res.json()) as { authenticated?: boolean; uid?: string; email?: string };
    return {
      authenticated: !!data.authenticated,
      available: true,
      uid: data.uid,
      email: data.email,
    };
  } catch {
    return { authenticated: false, available: false };
  }
}

/** Redirect to the server-driven login. Already-signed-in users (SSO) bounce
 *  straight back to `returnPath`. */
export function login(returnPath = '/'): void {
  const url = `${config.apiBaseUrl}/auth/login?return=${encodeURIComponent(returnPath)}`;
  window.location.assign(url);
}

/**
 * Complete the login. The OAuth client's registered redirect_uri is on the SPA
 * host (`https://portal.divako.dev/auth/callback`), so the IdP lands the
 * browser here with `?code&state`. The SPA holds no OAuth state — forward the
 * whole query string to portal-api, which validates state (its `portal_login`
 * cookie rides along: same-site, top-level GET), exchanges the code with the
 * server-side client_secret, sets the session cookie and 302s back to the SPA.
 * Must be a full navigation, NOT an XHR, so the Set-Cookie + redirect work.
 */
export function forwardLoginCallback(): void {
  window.location.replace(`${config.apiBaseUrl}/auth/callback${window.location.search}`);
}

/**
 * Sign out of portal-api AND of login.divako.io. Clearing only portal-api's
 * cookie isn't enough: login's SSO cookie survives, so `RequireSession` would
 * bounce through `/api/auth/login` and sign the user straight back in. The
 * response's `logoutUrl` is login's end-session URL, which returns here.
 */
export async function logout(): Promise<void> {
  let logoutUrl = '';
  try {
    const res = await fetch(`${config.apiBaseUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (res.ok) logoutUrl = ((await res.json()) as { logoutUrl?: string }).logoutUrl ?? '';
  } catch {
    /* ignore — clear client state regardless */
  }
  window.location.assign(logoutUrl || '/');
}
